// src/utils/clinicalNotesFormatter.js

/**
 * Util: formatea una fecha legible a partir de un campo date/datetime.
 */
const formatNoteDate = (note) => {
  if (!note || !note.noteDateTime) return 'N/A';
  try {
    return new Date(note.noteDateTime).toISOString();
  } catch (e) {
    return String(note.noteDateTime);
  }
};

/**
 * Util: vuelca TODOS los fields de la nota como texto estructurado.
 * Así no perdemos ninguna columna aunque no la hayamos mapeado a mano.
 */
const formatAllFieldsBlock = (fields = {}) => {
  if (!fields || typeof fields !== 'object') return 'No structured fields available.';

  const entries = Object.entries(fields)
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
    .sort(([a], [b]) => a.localeCompare(b));

  if (entries.length === 0) {
    return 'No structured fields available.';
  }

  const lines = entries.map(([key, value]) => `- ${key}: ${String(value)}`);

  return lines.join('\n');
};

/**
 * ========= ANNUAL WELLNESS 2.0 =========
 * Bloque textual para Annual Wellness (AWV).
 */
const formatAnnualWellnessBlock = (notes = []) => {
  if (!Array.isArray(notes) || notes.length === 0) return '';

  const blocks = notes.map((note, idx) => {
    const f = note.fields || {};
    const dateLabel = formatNoteDate(note);

    return [
      `NOTE #${idx + 1} – ${note.templateName || 'Annual Wellness 2.0'} – ${dateLabel}`,
      ``,
      `Chief complaints:`,
      (f.ChiefComplaints || f.ChiefComplaint || 'N/A'),
      ``,
      `History / HPI:`,
      (f.HPI || f.HistoryOfPresentIllness || f.History || 'N/A'),
      ``,
      `Past medical history (PMH):`,
      (f.PMH || f.MedicalHistory || 'N/A'),
      ``,
      `Assessment & Plan:`,
      (f.Assessments || f.AssessmentAndPlan || 'N/A'),
      ``,
      `Current medications (AWV note):`,
      (f.CurrentMedications || f.Medications || 'N/A'),
      ``,
      `Preventive care / Screening:`,
      (f.PreventiveCare || f.Screenings || 'N/A'),
      ``,
      `All structured fields (raw dump):`,
      formatAllFieldsBlock(f),
    ].join('\n');
  });

  return blocks.join('\n\n---\n\n');
};

/**
 * ========= FOLLOW UP EXAM 2.0 =========
 */
const formatFollowUpExamBlock = (notes = []) => {
  if (!Array.isArray(notes) || notes.length === 0) return '';

  const blocks = notes.map((note, idx) => {
    const f = note.fields || {};
    const dateLabel = formatNoteDate(note);

    const bpCurrent =
      f.Vitals_BP_Systolic1 && f.Vitals_BP_Diastolic1
        ? `${f.Vitals_BP_Systolic1}/${f.Vitals_BP_Diastolic1}`
        : '';

    const bpPrev =
      f.Vitals_BP_SystolicPrev && f.Vitals_BP_DiastolicPrev
        ? `${f.Vitals_BP_SystolicPrev}/${f.Vitals_BP_DiastolicPrev}`
        : '';

    return [
      `NOTE #${idx + 1} – ${note.templateName || 'Follow Up Exam 2.0'} – ${dateLabel}`,
      ``,
      `Vitals (from note):`,
      `- Weight: ${f.Vitals_Weight || '—'}`,
      `- Height: ${f.Vitals_Height || '—'}`,
      `- BMI: ${f.Vitals_BMI || '—'}`,
      `- Temperature: ${f.Vitals_Temperature || '—'}`,
      `- Pulse: ${f.Vitals_Pulse || '—'}`,
      `- O2 Sat RA: ${f.Vitals_O2SatRA || '—'}`,
      `- BP (current): ${bpCurrent || '—'}`,
      `- BP (previous): ${bpPrev || '—'}`,
      ``,
      `Chief complaints:`,
      (f.ChiefComplaints || 'N/A'),
      ``,
      `Current medications (note):`,
      (f.CurrentMedications || 'N/A'),
      ``,
      `Assessments:`,
      (f.Assessments || 'N/A'),
      ``,
      `Treatment plan:`,
      (f.Treatment || f.Treatment1 || 'N/A'),
      ``,
      `All structured fields (raw dump):`,
      formatAllFieldsBlock(f),
    ].join('\n');
  });

  return blocks.join('\n\n---\n\n');
};

/**
 * ========= TELEHEALTH 2.0 =========
 */
const formatTelehealthBlock = (notes = []) => {
  if (!Array.isArray(notes) || notes.length === 0) return '';

  const blocks = notes.map((note, idx) => {
    const f = note.fields || {};
    const dateLabel = formatNoteDate(note);

    return [
      `NOTE #${idx + 1} – ${note.templateName || 'Telehealth 2.0'} – ${dateLabel}`,
      ``,
      `HPI / Reason for visit:`,
      (f.HPI_Description || 'N/A'),
      ``,
      `Diagnosis:`,
      (f.DX_Diagnosis || 'N/A'),
      ``,
      `Visit summary / Plan:`,
      (f.AP_VisitSummary || 'N/A'),
      ``,
      `Counseling – Nutrition:`,
      (f.ED_NutritionCounsel || 'N/A'),
      ``,
      `Counseling – Physical Activity:`,
      (f.ED_PhysicalActivityCounsel || 'N/A'),
      ``,
      `Allergies (from telehealth note):`,
      (f.Hx_Allergies || 'N/A'),
      ``,
      `Current medications (from telehealth note):`,
      (f.Hx_CurrentMeds || 'N/A'),
      ``,
      `Disclaimer:`,
      (f.Disclaimer || 'N/A'),
      ``,
      `All structured fields (raw dump):`,
      formatAllFieldsBlock(f),
    ].join('\n');
  });

  return blocks.join('\n\n---\n\n');
};

/**
 * ========= HOSPITAL DISCHARGE FOLLOW UP =========
 */
const formatHospitalDischargeBlock = (notes = []) => {
  if (!Array.isArray(notes) || notes.length === 0) return '';

  const blocks = notes.map((note, idx) => {
    const f = note.fields || {};
    const dateLabel = formatNoteDate(note);

    const bpCurrent =
      f.Vitals_BP_Systolic1 && f.Vitals_BP_Diastolic1
        ? `${f.Vitals_BP_Systolic1}/${f.Vitals_BP_Diastolic1}`
        : '';

    const bpPrev =
      f.Vitals_BP_SystolicPrev && f.Vitals_BP_DiastolicPrev
        ? `${f.Vitals_BP_SystolicPrev}/${f.Vitals_BP_DiastolicPrev}`
        : '';

    return [
      `NOTE #${idx + 1} – ${note.templateName || 'Hospital Discharge Follow Up'} – ${dateLabel}`,
      ``,
      `Location: ${f.location || '—'}`,
      `Extra location info: ${(f.location2 || f.location1 || '—')}`,
      ``,
      `Vitals at follow up:`,
      `- Weight: ${f.Vitals_Weight || '—'}`,
      `- Height: ${f.Vitals_Height || '—'}`,
      `- BMI: ${f.Vitals_BMI || '—'}`,
      `- Temperature: ${f.Vitals_Temperature || '—'}`,
      `- Pulse: ${f.Vitals_Pulse || '—'}`,
      `- O2 Sat RA: ${f.Vitals_O2SatRA || '—'}`,
      `- BP (current): ${bpCurrent || '—'}`,
      `- BP (previous): ${bpPrev || '—'}`,
      ``,
      `Reason for appointment:`,
      (f.ReasonforAppointment || 'N/A'),
      ``,
      `HPI / Hospital course:`,
      (f.HistoryofPresentIllness || 'N/A'),
      '',
      (f.HistoryofPresentIllness1 || ''),
      ``,
      `Key diagnoses at discharge:`,
      (f.PMH2 || f.PMH || 'N/A'),
      ``,
      `Current medications:`,
      (f.CurrentMedications || 'N/A'),
      ``,
      `Treatment plan / Follow up:`,
      (f.Treatment || 'N/A'),
      '',
      f.FollowUp ? `Follow-up: ${f.FollowUp}` : '',
      ``,
      `Assessments:`,
      (f.Assessments || 'N/A'),
      ``,
      `All structured fields (raw dump):`,
      formatAllFieldsBlock(f),
    ].join('\n');
  });

  return blocks.join('\n\n---\n\n');
};

/**
 * ================================
 * 🔹 BUNDLE GENÉRICO DE NOTAS CLÍNICAS
 * ================================
 *
 * Devuelve:
 *  - sections: array con { label, type, text }
 *  - fullText: string con todas las secciones concatenadas, listo para IA.
 */
export const buildClinicalNotesBundle = ({
  annualWellnessNotes = [],
  followUpExamNotes = [],
  telehealthNotes = [],
  hospitalDischargeNotes = [],
} = {}) => {
  const sections = [];

  if (annualWellnessNotes.length > 0) {
    sections.push({
      label: 'Annual Wellness 2.0',
      type: 'annualWellness',
      text: formatAnnualWellnessBlock(annualWellnessNotes),
    });
  }

  if (followUpExamNotes.length > 0) {
    sections.push({
      label: 'Follow Up Exam 2.0',
      type: 'followUp',
      text: formatFollowUpExamBlock(followUpExamNotes),
    });
  }

  if (telehealthNotes.length > 0) {
    sections.push({
      label: 'Telehealth 2.0',
      type: 'telehealth',
      text: formatTelehealthBlock(telehealthNotes),
    });
  }

  if (hospitalDischargeNotes.length > 0) {
    sections.push({
      label: 'Hospital Discharge Follow Up',
      type: 'hospitalDischarge',
      text: formatHospitalDischargeBlock(hospitalDischargeNotes),
    });
  }

  const fullText = sections
    .map(
      (s) =>
        `===== ${s.label.toUpperCase()} =====\n\n${(s.text || '').trim()}`
    )
    .join('\n\n\n');

  return {
    sections,
    fullText,
  };
};

// 🔹 Util genérico para formatear fechas simples
const formatIsoDate = (value) => {
  if (!value) return 'N/A';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toISOString();
  } catch (e) {
    return String(value);
  }
};

// 🔹 Dump genérico de un objeto (por si faltan campos)
const formatObjectDump = (obj) => {
  if (!obj || typeof obj !== 'object') return '';
  const entries = Object.entries(obj)
    .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
    .sort(([a], [b]) => a.localeCompare(b));

  if (entries.length === 0) return '';

  return entries.map(([key, v]) => `- ${key}: ${String(v)}`).join('\n');
};

/**
 * ========= MEDICATIONS – BLOQUE PARA PLAN DE TRATAMIENTO =========
 * Recibe el array crudo de medications (AdvancedMD).
 */
export const formatMedicationsBlock = (medications = []) => {
  if (!Array.isArray(medications) || medications.length === 0) {
    return 'No medications found for this patient.';
  }

  const lines = medications.map((med, idx) => {
    const name =
      med.drugName ||
      med.drugGenericName ||
      med.drug ||
      'Unknown medication';

    const strength = med.drugStrength || '';
    const doseForm = med.doseForm || '';
    const route = med.route || '';
    const frequency = med.frequency || '';
    const qty = med.quantity != null ? `Qty ${med.quantity}` : '';
    const refills =
      typeof med.refills === 'number' ? `${med.refills} refills` : '';
    const status = med.status || (med.currentMedFlag ? 'Current' : '');
    const start = med.startDate ? formatIsoDate(med.startDate) : '';
    const stop = med.stopDate ? formatIsoDate(med.stopDate) : '';
    const doctor = med.doctor || '';

    const header = `MED #${idx + 1}: ${name}${
      strength ? ` • ${strength}` : ''
    }`;

    const attrs = [];

    if (doseForm) attrs.push(`Dose form: ${doseForm}`);
    if (route) attrs.push(`Route: ${route}`);
    if (frequency) attrs.push(`Frequency: ${frequency}`);
    if (qty) attrs.push(qty);
    if (refills) attrs.push(refills);
    if (status) attrs.push(`Status: ${status}`);
    if (doctor) attrs.push(`Prescriber: ${doctor}`);
    if (start || stop) {
      attrs.push(
        `Dates: ${start || 'N/A'}${
          stop ? ` → ${stop}` : ''
        }`
      );
    }

    let block = header;
    if (attrs.length > 0) {
      block += '\n' + attrs.map((a) => `- ${a}`).join('\n');
    }

    const dump = formatObjectDump(med);
    if (dump) {
      block += '\n\nRaw medication fields (debug):\n' + dump;
    }

    return block;
  });

  return lines.join('\n\n---\n\n');
};


/**
 * ========= LAB RESULTS – BLOQUE PARA PLAN DE TRATAMIENTO =========
 * Recibe el array crudo de labs (AdvancedMD).
 */
export const formatLabsBlock = (labs = []) => {
  if (!Array.isArray(labs) || labs.length === 0) {
    return 'No laboratory results available for this patient.';
  }

  // Meta general (de la primera orden)
  const first = labs[0] || null;
  const metaLines = [];
  if (first) {
    const obs = first.obsDateTime ? formatIsoDate(first.obsDateTime) : null;
    const spec = first.specDateTime ? formatIsoDate(first.specDateTime) : null;
    const signAt = first.signAt ? formatIsoDate(first.signAt) : null;

    metaLines.push(
      `Lab company: ${first.companyName || '—'}`,
      `Observation date: ${obs || '—'}`,
      `Specimen date: ${spec || '—'}`,
      `Signed by: ${first.signBy || '—'}${signAt ? ` at ${signAt}` : ''}`
    );
  }

  // Agrupar por código + descripción (igual que el UI)
  const map = new Map();

  labs.forEach((item) => {
    const key = `${item.resultCode || ''}|${item.resultDesc || ''}`;
    if (!map.has(key)) {
      map.set(key, {
        resultCode: item.resultCode,
        resultDesc: item.resultDesc,
        values: [],
        units: item.resultUnits || '',
        range: item.resultRange || '',
        status: item.resultStatus || '',
      });
    }
    const group = map.get(key);
    if (item.resultValue && item.resultValue.trim() !== '') {
      group.values.push(item.resultValue.trim());
    }
    if (!group.units && item.resultUnits) group.units = item.resultUnits;
    if (!group.range && item.resultRange) group.range = item.resultRange;
    if (!group.status && item.resultStatus) group.status = item.resultStatus;
  });

  const grouped = Array.from(map.values());

  const blocks = grouped.map((group, idx) => {
    const header = `LAB PANEL #${idx + 1}: ${
      group.resultCode || '—'
    } – ${group.resultDesc || '—'}`;

    const valueText = group.values.length
      ? group.values.join(', ')
      : '—';

    const attrs = [
      `- Result(s): ${valueText}`,
      `- Units: ${group.units || '—'}`,
      `- Reference range: ${group.range || '—'}`,
      `- Status: ${group.status || '—'}`,
    ];

    return `${header}\n${attrs.join('\n')}`;
  });

  return [
    metaLines.length ? metaLines.join('\n') : '',
    blocks.join('\n\n---\n\n'),
  ]
    .filter(Boolean)
    .join('\n\n');
};


/**
 * ========= ALLERGIES – BLOQUE PARA PLAN DE TRATAMIENTO =========
 * Recibe el array crudo de alergias (AdvancedMD).
 */
export const formatAllergiesBlock = (allergies = []) => {
  if (!Array.isArray(allergies) || allergies.length === 0) {
    return 'No allergy records found for this patient.';
  }

  const isNKDA =
    allergies.length === 1 &&
    (allergies[0].allergy || '').toUpperCase().includes('NKDA');

  if (isNKDA) {
    const a = allergies[0];
    const updated = a.updateDate ? formatIsoDate(a.updateDate) : '—';
    const who = a.userName || '—';

    return [
      'NKDA – No Known Drug Allergies.',
      `Recorded by: ${who}`,
      `Last updated: ${updated}`,
    ].join('\n');
  }

  const lines = allergies.map((a, idx) => {
    const allergen = a.allergy || 'Unknown allergy';
    const reaction = a.reaction || '';
    const treatment = a.treatment || '';
    const status = a.status || '';
    const updated = a.updateDate ? formatIsoDate(a.updateDate) : '';
    const who = a.userName || '';

    const header = `ALLERGY #${idx + 1}: ${allergen}`;
    const attrs = [];

    if (reaction) attrs.push(`Reaction: ${reaction}`);
    if (treatment) attrs.push(`Treatment: ${treatment}`);
    if (status) attrs.push(`Status: ${status}`);
    if (who || updated) {
      attrs.push(
        `Recorded by: ${who || '—'}${
          updated ? ` at ${updated}` : ''
        }`
      );
    }

    let block = header;
    if (attrs.length > 0) {
      block += '\n' + attrs.map((x) => `- ${x}`).join('\n');
    }

    const dump = formatObjectDump(a);
    if (dump) {
      block += '\n\nRaw allergy fields (debug):\n' + dump;
    }

    return block;
  });

  return lines.join('\n\n---\n\n');
};


/**
 * ================================
 * 🔹 BUILDER PARA CONTEXTO DE PLAN DE TRATAMIENTO (Tab 3)
 * ================================
 *
 * Devuelve un string grande listo para IA, estructurado en secciones:
 *  - Clinical notes (light, mismo estilo HCC)
 *  - Current + historic medications
 *  - Recent labs
 *  - Allergies
 */
export const buildTreatmentPlanContextText = ({
  annualWellnessNotes = [],
  followUpExamNotes = [],
  telehealthNotes = [],
  hospitalDischargeNotes = [],
  medications = [],
  labs = [],
  allergies = [],
} = {}) => {
  const notesBundle = buildClinicalNotesBundle({
    annualWellnessNotes,
    followUpExamNotes,
    telehealthNotes,
    hospitalDischargeNotes,
  });

  const medsBlock = formatMedicationsBlockForTreatmentPlan(medications);
  const labsBlock = formatLabsBlockForTreatmentPlan(labs);
  const allergiesBlock = formatAllergiesBlockForTreatmentPlan(allergies);

  const parts = [];

  if (notesBundle.fullText && notesBundle.fullText.trim()) {
    parts.push(
      '===== CLINICAL NOTES (SUMMARY) =====',
      '',
      notesBundle.fullText.trim()
    );
  }

  parts.push(
    '',
    '===== MEDICATIONS =====',
    '',
    medsBlock.trim(),
    '',
    '===== LABORATORY RESULTS =====',
    '',
    labsBlock.trim(),
    '',
    '===== ALLERGIES =====',
    '',
    allergiesBlock.trim()
  );

  let full = parts.join('\n');
  const MAX_CHARS = 16000;
  if (full.length > MAX_CHARS) {
    full =
      full.slice(0, MAX_CHARS) +
      '\n\n[TRUNCATED FOR AI TREATMENT PLAN CONTEXT – rest of data omitted]';
  }

  return full;
};


/**
 * ================================
 * 🔹 BUILDER ESPECÍFICO PARA HCC
 * ================================
 */
export const buildHccClinicalNotesText = ({
  annualWellnessNotes = [],
  followUpExamNotes = [],
  telehealthNotes = [],
  hospitalDischargeNotes = [],
} = {}) => {
  const sections = [];

  // --- AWV LIGHT ---
  const buildAwvText = (notes = []) =>
    notes
      .map((note, idx) => {
        const f = note.fields || {};
        const dateLabel = formatNoteDate(note);

        return [
          `NOTE #${idx + 1} – ${note.templateName || 'Annual Wellness 2.0'} – ${dateLabel}`,
          ``,
          `Chief complaints:`,
          (f.ChiefComplaints || f.ChiefComplaint || 'N/A'),
          ``,
          `History / HPI:`,
          (f.HPI || f.HistoryOfPresentIllness || f.History || 'N/A'),
          ``,
          `Past medical history (PMH):`,
          (f.PMH || f.MedicalHistory || 'N/A'),
          ``,
          `Assessment & Plan:`,
          (f.Assessments || f.AssessmentAndPlan || 'N/A'),
          ``,
          `Current medications:`,
          (f.CurrentMedications || f.Medications || 'N/A'),
        ].join('\n');
      })
      .join('\n\n---\n\n');

  // --- FOLLOW UP LIGHT ---
  const buildFollowUpText = (notes = []) =>
    notes
      .map((note, idx) => {
        const f = note.fields || {};
        const dateLabel = formatNoteDate(note);

        const bpCurrent =
          f.Vitals_BP_Systolic1 && f.Vitals_BP_Diastolic1
            ? `${f.Vitals_BP_Systolic1}/${f.Vitals_BP_Diastolic1}`
            : '';

        return [
          `NOTE #${idx + 1} – ${note.templateName || 'Follow Up Exam 2.0'} – ${dateLabel}`,
          ``,
          `Vitals:`,
          `- Weight: ${f.Vitals_Weight || '—'}`,
          `- BMI: ${f.Vitals_BMI || '—'}`,
          `- BP: ${bpCurrent || '—'}`,
          ``,
          `Chief complaints:`,
          (f.ChiefComplaints || 'N/A'),
          ``,
          `Current medications:`,
          (f.CurrentMedications || 'N/A'),
          ``,
          `Assessments:`,
          (f.Assessments || 'N/A'),
          ``,
          `Treatment plan:`,
          (f.Treatment || f.Treatment1 || 'N/A'),
        ].join('\n');
      })
      .join('\n\n---\n\n');

  // --- TELEHEALTH LIGHT ---
  const buildTelehealthText = (notes = []) =>
    notes
      .map((note, idx) => {
        const f = note.fields || {};
        const dateLabel = formatNoteDate(note);

        return [
          `NOTE #${idx + 1} – ${note.templateName || 'Telehealth 2.0'} – ${dateLabel}`,
          ``,
          `HPI / Reason for visit:`,
          (f.HPI_Description || 'N/A'),
          ``,
          `Diagnosis:`,
          (f.DX_Diagnosis || 'N/A'),
          ``,
          `Visit summary / Plan:`,
          (f.AP_VisitSummary || 'N/A'),
          ``,
          `Allergies:`,
          (f.Hx_Allergies || 'N/A'),
          ``,
          `Current medications:`,
          (f.Hx_CurrentMeds || 'N/A'),
        ].join('\n');
      })
      .join('\n\n---\n\n');

  // --- HOSPITAL FU LIGHT ---
  const buildHospitalFuText = (notes = []) =>
    notes
      .map((note, idx) => {
        const f = note.fields || {};
        const dateLabel = formatNoteDate(note);

        const bpCurrent =
          f.Vitals_BP_Systolic1 && f.Vitals_BP_Diastolic1
            ? `${f.Vitals_BP_Systolic1}/${f.Vitals_BP_Diastolic1}`
            : '';

        return [
          `NOTE #${idx + 1} – ${note.templateName || 'Hospital Discharge Follow Up'} – ${dateLabel}`,
          ``,
          `Reason for appointment:`,
          (f.ReasonforAppointment || 'N/A'),
          ``,
          `HPI / Hospital course:`,
          (f.HistoryofPresentIllness || 'N/A'),
          ``,
          `Key diagnoses at discharge:`,
          (f.PMH2 || f.PMH || 'N/A'),
          ``,
          `Vitals:`,
          `- Weight: ${f.Vitals_Weight || '—'}`,
          `- BMI: ${f.Vitals_BMI || '—'}`,
          `- BP: ${bpCurrent || '—'}`,
          ``,
          `Current medications:`,
          (f.CurrentMedications || 'N/A'),
          ``,
          `Treatment plan / Follow up:`,
          (f.Treatment || 'N/A'),
        ].join('\n');
      })
      .join('\n\n---\n\n');

  // Construimos secciones por tipo
  if (annualWellnessNotes.length > 0) {
    sections.push(
      `===== ANNUAL WELLNESS 2.0 =====\n\n${buildAwvText(annualWellnessNotes)}`
    );
  }

  if (followUpExamNotes.length > 0) {
    sections.push(
      `===== FOLLOW UP EXAM 2.0 =====\n\n${buildFollowUpText(
        followUpExamNotes
      )}`
    );
  }

  if (telehealthNotes.length > 0) {
    sections.push(
      `===== TELEHEALTH 2.0 =====\n\n${buildTelehealthText(telehealthNotes)}`
    );
  }

  if (hospitalDischargeNotes.length > 0) {
    sections.push(
      `===== HOSPITAL DISCHARGE FOLLOW UP =====\n\n${buildHospitalFuText(
        hospitalDischargeNotes
      )}`
    );
  }

  let fullText = sections.join('\n\n\n');

  // 🔪 Corte de seguridad por tamaño para la IA
  const MAX_CHARS = 12000;
  if (fullText.length > MAX_CHARS) {
    fullText =
      fullText.slice(0, MAX_CHARS) +
      '\n\n[TRUNCATED FOR AI HCC ANALYSIS – rest of notes omitted]';
  }

  return fullText;
};


/**
 * ================================
 * 🔹 BUILDER SOLO AWV (compatibilidad)
 * ================================
 */
export const buildAnnualWellnessClinicalNotesText = (annualWellnessNotes = []) => {
  const { fullText } = buildClinicalNotesBundle({
    annualWellnessNotes,
  });

  return fullText;
};


/**
 * ========= MEDICATIONS BLOCK (for Treatment Plan) =========
 */
const formatMedicationsBlockForTreatmentPlan = (medications = []) => {
  if (!Array.isArray(medications) || medications.length === 0) {
    return 'No medication records available.';
  }

  const lines = medications.map((m, idx) => {
    const start = m.startDate ? new Date(m.startDate).toISOString().slice(0, 10) : 'N/A';
    const stop = m.stopDate ? new Date(m.stopDate).toISOString().slice(0, 10) : 'N/A';

    const status = (m.status || '').toLowerCase();
    const isCurrent = m.currentMedFlag || status.startsWith('current');

    return [
      `MED #${idx + 1} – ${isCurrent ? 'CURRENT' : 'HISTORIC'}`,
      `Drug: ${m.drugName || 'N/A'} ${m.drugStrength ? '(' + m.drugStrength + ')' : ''}`,
      `Form/Route: ${(m.doseForm || '') + (m.route ? ' • ' + m.route : '') || 'N/A'}`,
      `Frequency: ${m.frequency || 'N/A'}`,
      `Quantity: ${m.quantity != null ? String(m.quantity) : 'N/A'}`,
      `Refills: ${
        typeof m.refills === 'number' ? String(m.refills) : 'N/A'
      }`,
      `Status: ${m.status || 'N/A'}`,
      `Prescriber: ${m.doctor || 'N/A'}`,
      `Start date: ${start}`,
      `Stop date: ${stop}`,
    ].join('\n');
  });

  return lines.join('\n\n---\n\n');
};

// ========= DEMOGRAPHICS BLOCK =========
const formatDemographicsBlock = (demographics = {}) => {
  if (!demographics || typeof demographics !== 'object') {
    return 'No demographics available.';
  }

  const {
    firstName,
    lastName,
    sex,
    gender,
    dateOfBirth,
    dob,
    age,
    mrn,
    memberId,
    language,
  } = demographics;

  const isoDob = dateOfBirth || dob
    ? formatIsoDate(dateOfBirth || dob)
    : 'N/A';

  const lines = [
    `Name: ${(firstName || lastName) ? `${firstName || ''} ${lastName || ''}`.trim() : 'N/A'}`,
    `Sex/Gender: ${sex || gender || 'N/A'}`,
    `Age: ${age != null ? String(age) : 'N/A'}`,
    `Date of birth: ${isoDob}`,
    `MRN / Member ID: ${mrn || memberId || 'N/A'}`,
    `Preferred language: ${language || 'N/A'}`,
  ];

  return lines.join('\n');
};

// ========= SIMPLE VITALS SUMMARY FROM NOTES =========
const collectBpSnapshotsFromNotes = (notes = []) => {
  const snapshots = [];

  notes.forEach((note) => {
    const f = note.fields || {};
    const date = formatNoteDate(note);

    const sys = f.Vitals_BP_Systolic1 || f.BP_Systolic || null;
    const dia = f.Vitals_BP_Diastolic1 || f.BP_Diastolic || null;
    const bmi = f.Vitals_BMI || null;

    if (sys && dia) {
      snapshots.push({
        date,
        bp: `${sys}/${dia}`,
        bmi,
        source: note.templateName || 'Clinical note',
      });
    }
  });

  return snapshots;
};

const formatVitalsSummaryForHedis = ({
  annualWellnessNotes = [],
  followUpExamNotes = [],
  hospitalDischargeNotes = [],
} = {}) => {
  const allNotes = []
    .concat(annualWellnessNotes || [])
    .concat(followUpExamNotes || [])
    .concat(hospitalDischargeNotes || []);

  const bps = collectBpSnapshotsFromNotes(allNotes);

  if (!bps.length) {
    return 'No clear blood pressure / BMI readings found in structured notes.';
  }

  const lines = bps.slice(0, 8).map((snap, idx) => {
    return [
      `BP SNAPSHOT #${idx + 1}`,
      `Date: ${snap.date}`,
      `Source: ${snap.source}`,
      `BP: ${snap.bp}`,
      `BMI: ${snap.bmi || 'N/A'}`,
    ].join('\n');
  });

  return lines.join('\n\n---\n\n');
};


/**
 * ========= LABS BLOCK (for Treatment Plan) =========
 */
const formatLabsBlockForTreatmentPlan = (labs = []) => {
  if (!Array.isArray(labs) || labs.length === 0) {
    return 'No laboratory results available.';
  }

  const lines = labs.map((lab, idx) => {
    const obsDate = lab.obsDateTime
      ? new Date(lab.obsDateTime).toISOString()
      : 'N/A';
    const specDate = lab.specDateTime
      ? new Date(lab.specDateTime).toISOString()
      : 'N/A';
    const signAt = lab.signAt
      ? new Date(lab.signAt).toISOString()
      : 'N/A';

    return [
      `LAB #${idx + 1}`,
      `Company: ${lab.companyName || 'N/A'}`,
      `Observation date: ${obsDate}`,
      `Specimen date: ${specDate}`,
      `Signed by: ${lab.signBy || 'N/A'} at ${signAt}`,
      `Result code: ${lab.resultCode || 'N/A'}`,
      `Description: ${lab.resultDesc || 'N/A'}`,
      `Value: ${lab.resultValue || 'N/A'}`,
      `Units: ${lab.resultUnits || 'N/A'}`,
      `Reference range: ${lab.resultRange || 'N/A'}`,
      `Status: ${lab.resultStatus || 'N/A'}`,
    ].join('\n');
  });

  return lines.join('\n\n---\n\n');
};

/**
 * ================================
 * 🔹 BUILDER ESPECÍFICO PARA HEDIS
 * ================================
 *
 * Arma un contexto compacto en inglés, pensado para evaluación de métricas:
 *  - Patient demographics
 *  - Key vitals (BP / BMI snapshots)
 *  - Clinical notes (bundle ligero, igual que HCC)
 *  - Medications (tratamiento crónico)
 *  - Labs (agrupados)
 *  - Allergies
 */
export const buildHedisContextText = ({
  demographics = {},
  annualWellnessNotes = [],
  followUpExamNotes = [],
  telehealthNotes = [],
  hospitalDischargeNotes = [],
  medications = [],
  labs = [],
  allergies = [],
} = {}) => {
  const parts = [];

  // 1) Demografía
  parts.push(
    '===== PATIENT DEMOGRAPHICS =====',
    '',
    formatDemographicsBlock(demographics).trim()
  );

  // 2) Vitals: BP / BMI
  const vitalsBlock = formatVitalsSummaryForHedis({
    annualWellnessNotes,
    followUpExamNotes,
    hospitalDischargeNotes,
  });
  parts.push(
    '',
    '===== VITALS – BLOOD PRESSURE & BMI (from notes) =====',
    '',
    vitalsBlock.trim()
  );

  // 3) Notas clínicas (bundle genérico ya existente)
  const notesBundle = buildClinicalNotesBundle({
    annualWellnessNotes,
    followUpExamNotes,
    telehealthNotes,
    hospitalDischargeNotes,
  });

  if (notesBundle.fullText && notesBundle.fullText.trim()) {
    parts.push(
      '',
      '===== CLINICAL NOTES (STRUCTURED SUMMARY) =====',
      '',
      notesBundle.fullText.trim()
    );
  }

  // 4) Medicaciones (usamos bloque para Treatment Plan)
  const medsBlock = formatMedicationsBlockForTreatmentPlan(medications);
  parts.push(
    '',
    '===== MEDICATIONS (CURRENT + HISTORIC) =====',
    '',
    medsBlock.trim()
  );

  // 5) Laboratorios (usamos bloque agrupado)
  const labsBlock = formatLabsBlockForTreatmentPlan(labs);
  parts.push(
    '',
    '===== LABORATORY RESULTS (GROUPED) =====',
    '',
    labsBlock.trim()
  );

  // 6) Alergias
  const allergiesBlock = formatAllergiesBlockForTreatmentPlan(allergies);
  parts.push(
    '',
    '===== ALLERGIES =====',
    '',
    allergiesBlock.trim()
  );

  let full = parts.join('\n');

  // Cortamos por seguridad para la IA
  const MAX_CHARS = 16000;
  if (full.length > MAX_CHARS) {
    full =
      full.slice(0, MAX_CHARS) +
      '\n\n[TRUNCATED FOR AI HEDIS CONTEXT – rest of data omitted]';
  }

  return full;
};


/**
 * ================================
 * 🔹 BUILDER PARA CONTEXTO DE IA – MEDICATION DETAIL TABLE
 * ================================
 *
 * Usa:
 *  - Notas clínicas (solo necesitamos que incluyan las secciones de medicamentos)
 *  - Lista estructurada de medicamentos (AdvancedMD)
 *  - Alergias (para contexto de seguridad)
 */
export const buildMedicationDetailContextText = ({
  annualWellnessNotes = [],
  followUpExamNotes = [],
  telehealthNotes = [],
  hospitalDischargeNotes = [],
  medications = [],
  allergies = [],
} = {}) => {
  // 1) Notas clínicas completas, en el mismo estilo que ya usas
  const notesBundle = buildClinicalNotesBundle({
    annualWellnessNotes,
    followUpExamNotes,
    telehealthNotes,
    hospitalDischargeNotes,
  });

  // 2) Bloque detallado de medicamentos (ya lo tienes)
  const medsBlock = formatMedicationsBlockForTreatmentPlan(medications);

  // 3) Alergias (importante para interacciones / BBW)
  const allergiesBlock = formatAllergiesBlockForTreatmentPlan(allergies);

  const parts = [];

  if (notesBundle.fullText && notesBundle.fullText.trim()) {
    parts.push(
      '===== CLINICAL NOTES (MEDICATION-RELATED CONTEXT) =====',
      '',
      notesBundle.fullText.trim()
    );
  }

  parts.push(
    '',
    '===== STRUCTURED MEDICATION LIST (EHR) =====',
    '',
    medsBlock.trim(),
    '',
    '===== ALLERGIES (SAFETY CONTEXT) =====',
    '',
    allergiesBlock.trim()
  );

  let full = parts.join('\n');

  // 🔪 Corte de seguridad por tamaño
  const MAX_CHARS = 16000;
  if (full.length > MAX_CHARS) {
    full =
      full.slice(0, MAX_CHARS) +
      '\n\n[TRUNCATED FOR AI MEDICATION DETAIL CONTEXT – rest of data omitted]';
  }

  return full;
};

/**
 * ========= ALLERGIES BLOCK (for Treatment Plan) =========
 */
const formatAllergiesBlockForTreatmentPlan = (allergies = []) => {
  if (!Array.isArray(allergies) || allergies.length === 0) {
    return 'No allergy records found.';
  }

  const isNKDA =
    allergies.length === 1 &&
    (allergies[0].allergy || '').toUpperCase().includes('NKDA');

  if (isNKDA) {
    const a = allergies[0];
    const updated = a.updateDate
      ? new Date(a.updateDate).toISOString()
      : 'N/A';

    return [
      'NKDA – No Known Drug Allergies',
      `Recorded by: ${a.userName || 'N/A'}`,
      `Last updated: ${updated}`,
    ].join('\n');
  }

  const lines = allergies.map((a, idx) => {
    const updated = a.updateDate
      ? new Date(a.updateDate).toISOString()
      : 'N/A';

    const parts = [];
    if (a.reaction) parts.push(`Reaction: ${a.reaction}`);
    if (a.treatment) parts.push(`Treatment: ${a.treatment}`);
    if (a.status) parts.push(`Status: ${a.status}`);

    return [
      `ALLERGY #${idx + 1}`,
      `Name: ${a.allergy || 'Unknown allergy'}`,
      `Details: ${parts.length ? parts.join(' | ') : 'N/A'}`,
      `Updated at: ${updated}`,
      `Recorded by: ${a.userName || 'N/A'}`,
    ].join('\n');
  });

  return lines.join('\n\n---\n\n');
};


